import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import clsx from 'clsx';

import { useAiEditActions } from '@/utils/hooks/scheduler/aiCaption/useAiEditActions';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Utils
import { put } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import { useMixPanelTrackLinks } from '@/utils/mixpanel/useMixPanelTrackLinks';
// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

import PropTypes from 'prop-types';

// Lib
import { MEDIA_LIBRARY_TYPES } from '@/lib/media/mediaLibraryTypes';
import { mediaLibraryCreateSchedulerPost } from '@/lib/mixpanel/trackLinks';
// Endpoints
import { PUT_MEDIA_LIBRARY } from '@/lib/routes/apiEndpoints';
import { SCHEDULER } from '@/lib/routes/privateRoutes';
import { getCaptionRestriction } from '@/lib/scheduler/postFunnel/captionRestrictions';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Label from '@/components/base/Label';
import SimpleVideoPlayer from '@/components/base/SimpleVideoPlayer';
import ActionTag from '@/components/base/Tag/ActionTag';
import { CaptionPreview } from '@/components/composite/CaptionPreview';
import { PreviewOfDocument } from '@/components/composite/Scheduler/PostFunnel/MediaPreview/Document/PreviewOfDocument';
import { SchedulerCaption } from '@/components/composite/Scheduler/PostFunnel/SchedulerCaption';
import DeleteCaptionModal from '@/components/pages/media/modals/DeleteCaptionModal';
import DeleteMediaModal from '@/components/pages/media/modals/DeleteMediaModal';
import AddTags from '@/components/pages/media/components/AddTags';
import TagsList from '@/components/pages/media/components/TagsList';

// For popper of AddTags
const MEDIA_VIEWER_ID = 'media-viewer-content';

const MediaLibraryViewerContent = ({
    media,
    mutateLibraryMedia,
    deleteSuccess,
    toggleModal,
    availableTags
}) => {
    const { t } = useTranslation('media');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { isOpen: isDeleteModalOpen, toggle: toggleDeleteModal } = useToggle();
    const { isOpen: isDeleteCaptionModalOpen, toggle: toggleDeleteCaptionModal } = useToggle();

    //track create scheduler post
    useMixPanelTrackLinks(mediaLibraryCreateSchedulerPost);

    const getInitialEditCaptionState = () => ({
        caption: media.caption,
        previousCaption: null,
        aiEditAction: null,
        aiEditActionError: null,
        mentions: media.mentions,
        status: { valid: true, warning: false },
        isEditing: media.caption === null,
        isLoading: false,
        hasError: false
    });

    useEffect(() => {
        setEditCaptionState(getInitialEditCaptionState);
    }, [media]);

    const [editCaptionState, setEditCaptionState] = useState(getInitialEditCaptionState);
    const network = activeSocialProfile?.social_network_type;
    const profileId = activeSocialProfile?.id;

    const updateCaptionAfterAiAction = (caption) => {
        setEditCaptionState((prevState) => ({
            ...prevState,
            caption: caption,
            previousCaption: prevState.caption ?? ''
        }));
    };

    const clearAiEditError = () => () =>
        setEditCaptionState((prevState) => ({
            ...prevState,
            aiEditActionError: null
        }));

    const rollbackToPreviousCaption = () => {
        setEditCaptionState((prevState) => ({
            ...prevState,
            caption: prevState.previousCaption,
            previousCaption: null
        }));
    };

    const {
        handleEnhanceCaption,
        handleHashtags,
        handleToneOfVoiceCaption,
        handleTranslateCaption
    } = useAiEditActions({
        captionValue: editCaptionState.caption,
        clearError: clearAiEditError,
        errorCallback: (error) =>
            setEditCaptionState((prevState) => ({
                ...prevState,
                aiEditActionError: error
            })),
        isProcessingAction: (action) =>
            setEditCaptionState((prevState) => ({
                ...prevState,
                aiEditAction: action
            })),
        profileId: profileId,
        successCallback: updateCaptionAfterAiAction
    });

    const updateCaption = async (caption, mentions) => {
        const { data: updatedMedia } = await put(
            injectEndpointUrlParams(PUT_MEDIA_LIBRARY, {
                socialProfil: activeSocialProfile.id,
                libraryMedia: media.id
            }),
            {
                caption: caption === '' ? null : caption,
                mentions: mentions
            }
        );
        mutateLibraryMedia(
            (prevState) =>
                prevState.map((page) => ({
                    ...page,
                    data: page.data.map((mediaItem) => {
                        if (mediaItem.id === media.id) {
                            return updatedMedia.data;
                        }

                        return mediaItem;
                    })
                })),
            false
        );
    };

    const saveCaption = async () => {
        setEditCaptionState((prevState) => ({
            ...prevState,
            isLoading: true,
            hasError: false
        }));
        try {
            await updateCaption(editCaptionState.caption, editCaptionState.mentions);
            setEditCaptionState((prevState) => ({
                ...prevState,
                isEditing: false,
                isLoading: false
            }));
        } catch (e) {
            setEditCaptionState((prevState) => ({
                ...prevState,
                isLoading: false,
                hasError: true
            }));
        }
    };

    return (
        <>
            <div id={MEDIA_VIEWER_ID} className="relative flex h-full flex-col md:flex-row">
                <div className="w-full px-10 py-10 md:w-1/2 md:px-16 md:py-20 lg:px-24">
                    <div className="w-full md:ml-auto md:max-w-[32rem]">
                        <div
                            className={clsx(
                                'relative flex w-full flex-col items-center justify-center overflow-hidden rounded-md bg-icoGray-100 dark:bg-icoDarkMode-deepBlack',
                                {
                                    square: media.media_type !== MEDIA_LIBRARY_TYPES.TYPE_DOCUMENT
                                }
                            )}>
                            {media.media_type === MEDIA_LIBRARY_TYPES.TYPE_VIDEO ? (
                                <SimpleVideoPlayer
                                    thumbnail={
                                        media?.medias?.[0]?.min_thumbnail ??
                                        media?.medias?.[0]?.thumbnail
                                    }
                                    video={media?.medias[0].video}
                                    className="flex h-full w-full items-center justify-center"
                                />
                            ) : media.media_type === MEDIA_LIBRARY_TYPES.TYPE_DOCUMENT ? (
                                <div className="w-full">
                                    <PreviewOfDocument document={media?.medias?.[0]} />
                                </div>
                            ) : (
                                <Img
                                    src={
                                        media?.medias?.[0]?.min_thumbnail ??
                                        media?.medias?.[0]?.image
                                    }
                                    mediaId={media.id}
                                />
                            )}
                        </div>
                        <div className="mt-8 flex flex-wrap items-center gap-2">
                            <TagsList
                                tags={media.tags}
                                selectedMediaIds={[media.id]}
                                mutateLibraryMedia={mutateLibraryMedia}
                            />
                            <AddTags
                                selectedMediaIds={[media.id]}
                                availableTags={availableTags}
                                mediaTags={media.tags}
                                mutateLibraryMedia={mutateLibraryMedia}
                                container={document.getElementById(MEDIA_VIEWER_ID)}
                                renderButton={(setReferenceElement, toggle) => (
                                    <div ref={setReferenceElement}>
                                        <ActionTag
                                            className="cursor-pointer"
                                            onClick={toggle}
                                            afterIcon="plus">
                                            <Icon icon="labels" />
                                        </ActionTag>
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex w-full bg-icoGray-100 px-10 py-10 dark:bg-icoDarkMode-anthracite md:w-1/2 md:px-16 md:py-20 lg:px-24">
                    <div className="flex w-full flex-col md:max-w-[32rem]">
                        {!editCaptionState.isEditing ? (
                            <div className="flex items-center justify-between">
                                <Label
                                    htmlFor={`media-library-${network}-caption`}
                                    className="!w-auto cursor-pointer">
                                    <span className="text-base">
                                        <Icon icon="captions" />
                                        <span className="ml-3 font-GSemiBold text-black dark:text-icoDarkMode-wolf">
                                            {t('add_caption')}
                                        </span>
                                    </span>
                                </Label>
                            </div>
                        ) : null}

                        {editCaptionState.hasError ? (
                            <InfoBanner className="mt-5">
                                {t('errors:error_media_library.caption.error_occured_edit_caption')}
                            </InfoBanner>
                        ) : null}

                        {editCaptionState.isEditing ? (
                            <>
                                <div className="w-full">
                                    <SchedulerCaption
                                        className="rounded-md bg-white"
                                        captionWrapperStyle="media_library"
                                        captionLayoutConfig={{
                                            hasAvatar: true,
                                            hasBorder: false,
                                            aiButton: true,
                                            label: (
                                                <Label
                                                    htmlFor={`media-library-${network}-caption`}
                                                    className="!w-auto cursor-pointer"
                                                    fontType="scheduler">
                                                    <span className="text-base">
                                                        <Icon icon="captions" />
                                                        <span className="ml-3 font-GSemiBold text-black dark:text-icoDarkMode-wolf">
                                                            {t('add_caption')}
                                                        </span>
                                                    </span>
                                                </Label>
                                            )
                                        }}
                                        rootId={'content-step'}
                                        valid={editCaptionState.status}
                                        validateCaptionCallback={(status) => {
                                            setEditCaptionState((prevState) => ({
                                                ...prevState,
                                                status
                                            }));
                                        }}
                                        initialRestrictions={getCaptionRestriction(
                                            network,
                                            media.media_type === MEDIA_LIBRARY_TYPES.TYPE_PHOTO
                                                ? 'image'
                                                : media.media_type
                                        )}
                                        network={network}
                                        mentions={editCaptionState.mentions}
                                        updateMentions={(mentions) => {
                                            setEditCaptionState((prevState) => ({
                                                ...prevState,
                                                mentions
                                            }));
                                        }}
                                        caption={editCaptionState.caption ?? ''}
                                        updateCaption={(caption) => {
                                            setEditCaptionState((prevState) => ({
                                                ...prevState,
                                                caption
                                            }));
                                        }}
                                        inputProps={{
                                            id: `media-library-${network}-caption`,
                                            name: `media-library-${network}-caption`,
                                            placeholder: t('caption_placeholder')
                                        }}
                                        aiConfig={{
                                            currentAiAction: editCaptionState.aiEditAction,
                                            aiActionError: editCaptionState.aiEditActionError,
                                            hasPreviousAction: editCaptionState.previousCaption,
                                            previousCallback: rollbackToPreviousCaption,
                                            clearError: clearAiEditError,
                                            currentCaption: editCaptionState.caption ?? '',
                                            currentProfileId: profileId,
                                            isButtonDisabled: editCaptionState.aiEditAction,
                                            updateCaption: updateCaptionAfterAiAction,
                                            dropdownCallbacks: {
                                                handleEnhanceCaption,
                                                handleHashtags,
                                                handleToneOfVoiceCaption,
                                                handleTranslateCaption
                                            }
                                        }}
                                    />
                                </div>
                                <div className="mt-3 flex items-center justify-end space-x-5">
                                    {media.caption !== null && (
                                        <button
                                            className="text-xs text-icoBlue underline"
                                            onClick={() => {
                                                setEditCaptionState(getInitialEditCaptionState());
                                            }}>
                                            {t('cancel')}
                                        </button>
                                    )}
                                    <Button
                                        size="sm"
                                        disabled={
                                            !editCaptionState.status.valid ||
                                            media.caption === editCaptionState.caption
                                        }
                                        isLoading={editCaptionState.isLoading}
                                        onClick={() => {
                                            if (editCaptionState.status.valid) {
                                                saveCaption();
                                            }
                                        }}>
                                        {t('save_caption')}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="mt-4 flex space-x-5 rounded-md bg-white p-6 dark:bg-icoDarkMode-deepBlack">
                                    <Avatar
                                        url={activeSocialProfile?.external_api_data?.avatar}
                                        fallback={activeSocialProfile?.slug?.[0]}
                                        profileType={activeSocialProfile?.social_network_type}
                                    />
                                    <CaptionPreview
                                        caption={media.caption ?? ''}
                                        network={network}
                                        mentions={media.mentions}
                                        className="text-black"
                                    />
                                </div>
                                <div className="mt-2 flex justify-end space-x-5 text-xs">
                                    <button
                                        type="button"
                                        className="text-icoBlue"
                                        onClick={() => {
                                            setEditCaptionState((prevState) => ({
                                                ...prevState,
                                                isEditing: true
                                            }));
                                        }}>
                                        {t('edit_caption')}
                                    </button>
                                    <button
                                        type="button"
                                        className="text-icoGray-500"
                                        onClick={toggleDeleteCaptionModal}>
                                        {t('remove_caption')}
                                    </button>
                                </div>
                            </>
                        )}

                        <div className="mt-8 flex flex-1 items-end justify-evenly gap-4">
                            <Button
                                variant="secondary"
                                icon="trash"
                                onClick={toggleDeleteModal}
                                className="w-full">
                                {t('delete_media')}
                            </Button>

                            <Button
                                as={Link}
                                className="w-full"
                                href={{
                                    pathname: SCHEDULER,
                                    query: { library_media_id: media.id }
                                }}
                                id="media-library-create-post">
                                {t('create_post')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <DeleteMediaModal
                isOpen={isDeleteModalOpen}
                toggle={toggleDeleteModal}
                mediaIds={[media.id]}
                deleteSuccess={(mediaIds) => {
                    toggleModal();
                    deleteSuccess(mediaIds);
                }}
            />
            <DeleteCaptionModal
                isOpen={isDeleteCaptionModalOpen}
                toggle={toggleDeleteCaptionModal}
                deleteCaption={async () => {
                    await updateCaption(null, []);
                }}
            />
        </>
    );
};

export const Img = ({ mediaId, src }) => {
    const [errorImg, setErrorImage] = useState(false);

    if (errorImg)
        return (
            <div className="flex h-full w-full items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                {/* Change Icon to real fallback */}
                <Icon
                    icon="content"
                    className="text-8xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </div>
        );

    return (
        <Image
            src={src}
            alt={mediaId}
            onError={() => setErrorImage(true)}
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
        />
    );
};

Img.propTypes = {
    mediaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    src: PropTypes.string
};

export default MediaLibraryViewerContent;
